import { addDays, format, isBefore, startOfDay } from 'date-fns';
import { Location } from '~/core/types';
import { getHoursInRange, homologateDay } from '~/utils/date';

/**
 * Reglas de una reserva que se repite.
 *
 * Todo lo de aquí es cálculo puro: recibe datos y devuelve datos, sin tocar
 * Firestore ni React. Así se puede razonar sobre ello y probarlo suelto, que es
 * lo que importa cuando la consecuencia de equivocarse es que dos personas
 * acaben en la misma sala.
 *
 * La comprobación de disponibilidad de este módulo es solo para AVISAR. Quien
 * decide de verdad es la transacción de `crearSerie.ts`, que vuelve a mirar el
 * estado real de la sala en el momento de escribir.
 */

/** Tope duro: una reserva semanal durante un año entero. */
export const MAXIMO_FECHAS = 52;
/** Nadie planifica una sala con más de un año de antelación. */
export const MAXIMO_MESES = 12;

/** Índice de `weekly-dates`: 0 es lunes y 6 domingo, no como `getDay()`. */
export const diaDeLaSemana = (fecha: Date): number => homologateDay[fecha.getDay()];

export const NOMBRES_DIAS = [
  { indice: 0, corto: 'L', largo: 'lunes' },
  { indice: 1, corto: 'M', largo: 'martes' },
  { indice: 2, corto: 'X', largo: 'miércoles' },
  { indice: 3, corto: 'J', largo: 'jueves' },
  { indice: 4, corto: 'V', largo: 'viernes' },
  { indice: 5, corto: 'S', largo: 'sábado' },
  { indice: 6, corto: 'D', largo: 'domingo' },
];

/**
 * Fechas de la serie: los días de la semana elegidos, desde el día siguiente al
 * de la reserva original y hasta la fecha final incluida.
 *
 * La reserva original no se repite a sí misma; ya existe.
 */
export const fechasDeLaSerie = (
  desde: Date,
  diasElegidos: number[],
  hasta: Date,
  maximo = MAXIMO_FECHAS
): Date[] => {
  const fechas: Date[] = [];
  if (!diasElegidos.length) return fechas;

  const fin = startOfDay(hasta);
  let cursor = addDays(startOfDay(desde), 1);

  while (!isBefore(fin, cursor) && fechas.length < maximo) {
    if (diasElegidos.includes(diaDeLaSemana(cursor))) fechas.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return fechas;
};

/**
 * Franjas que ocupa una reserva concreta, leídas de la propia sala.
 *
 * No se recalculan a partir del horario: el formulario las deriva de reglas
 * enredadas (hora de fin opcional, `blockTimeMinutes`, día completo, y un par
 * de ajustes al principio y al final de la lista). Repetir aquí ese cálculo
 * sería repetir también sus rarezas y arriesgarse a que las copias ocupen algo
 * distinto del original. Lo que la sala dice que ocupa es la verdad.
 */
export const franjasQueOcupa = (
  location: Location | undefined,
  fecha: string,
  reservationId: string
): string[] => {
  const dia = (location?.reservations?.[fecha] ?? {}) as Record<string, string[]>;
  return Object.entries(dia)
    .filter(([, ids]) => Array.isArray(ids) && ids.includes(reservationId))
    .map(([hora]) => hora)
    .sort();
};

/** Plazas que consume la reserva en cada franja, según cómo esté configurada la sala. */
export const plazasPorFranja = (location: Location | undefined, personas: number): number => {
  const ajustes = location?.schedule?.settings;
  const porPersona = ajustes?.personHasSpecificPosition || ajustes?.isReservationWholeDay;
  return porPersona ? Math.max(personas, 1) : 1;
};

export type EstadoFecha = 'libre' | 'ocupada' | 'cerrada' | 'bloqueada' | 'aforo';

export type Veredicto = {
  estado: EstadoFecha;
  /** Explicación para la persona, ya en español. */
  motivo?: string;
};

const VEREDICTO_LIBRE: Veredicto = { estado: 'libre' };

/**
 * ¿Cabe esta reserva en esta sala, este día, en estas franjas?
 *
 * Aplica las mismas reglas que el formulario, en el mismo orden:
 * horario del día → fechas bloqueadas → plazas libres en cada franja.
 *
 * `special-dates` no se mira, igual que no lo mira `useStartHours` al calcular
 * las horas; ninguna sede de NOI tiene ninguna (comprobado en producción el
 * 10/09/2026), así que replicarlo mantiene los dos caminos coherentes.
 */
export const evaluarFecha = ({
  location,
  fecha,
  franjas,
  personas,
  ignorarReservaId,
}: {
  location: Location | undefined;
  fecha: string;
  franjas: string[];
  personas: number;
  /** Al recolocar una reserva, no debe estorbarse a sí misma. */
  ignorarReservaId?: string;
}): Veredicto => {
  if (!location) return { estado: 'cerrada', motivo: 'La sala ya no está disponible' };
  if (!franjas.length) return { estado: 'cerrada', motivo: 'Sin horario que ocupar' };

  const [anio, mes, dia] = fecha.split('-').map(Number);
  const cuando = new Date(anio, mes - 1, dia);

  // 1) El horario del día de la semana.
  const horario = location.schedule?.['weekly-dates']?.[diaDeLaSemana(cuando)] as
    | { opening?: string; closing?: string; isOpen?: boolean }
    | undefined;
  if (!horario || horario.isOpen === false) {
    return { estado: 'cerrada', motivo: 'La sala no abre ese día' };
  }

  // Los espacios de día completo se reservan por jornadas, no por horas: al
  // guardarlos se ocupan las 48 franjas desde las 00:00 aunque el sitio abra
  // más tarde (comprobado en producción: la zona de coworking abre a la 01:00
  // y ocupa desde las 00:00). Medirlos contra el horario los daría por
  // cerrados siempre.
  const diaCompleto = Boolean(location.schedule?.settings?.isReservationWholeDay);
  if (!diaCompleto) {
    const abiertas = getHoursInRange(horario.opening ?? '00:00', horario.closing ?? '23:59');
    if (franjas.some((h) => !abiertas.includes(h))) {
      return {
        estado: 'cerrada',
        motivo: `Ese día abre de ${horario.opening} a ${horario.closing}`,
      };
    }
  }

  // 2) Bloqueos puntuales de esa fecha.
  const bloqueo = location.schedule?.['block-dates']?.[fecha] as
    | { open?: boolean; hours?: { from: string; to: string }[] }
    | undefined;
  if (bloqueo) {
    const bloqueadas = new Set(
      (bloqueo.hours ?? []).flatMap((h) => getHoursInRange(h.from, h.to))
    );
    if (franjas.some((h) => bloqueadas.has(h))) {
      return { estado: 'bloqueada', motivo: 'Hay un bloqueo a esa hora' };
    }
    // Un día marcado como cerrado y sin horas concretas cierra el día entero.
    if (bloqueo.open === false && !(bloqueo.hours ?? []).length) {
      return { estado: 'bloqueada', motivo: 'Ese día está bloqueado' };
    }
  }

  // 3) Plazas libres, la misma cuenta que hace la transacción al escribir.
  const limite = Number(location.schedule?.settings?.numberBookingsAllow ?? 1);
  const plazas = plazasPorFranja(location, personas);
  const ocupacion = (location.reservations?.[fecha] ?? {}) as Record<string, string[]>;

  for (const hora of franjas) {
    const ids = (ocupacion[hora] ?? []).filter((id) => id !== ignorarReservaId);
    if (ids.length + plazas > limite) {
      const libres = Math.max(limite - ids.length, 0);
      return {
        estado: limite > 1 ? 'aforo' : 'ocupada',
        motivo:
          limite > 1
            ? `A las ${hora} solo quedan ${libres} de ${limite} puestos`
            : `Ya hay una reserva a las ${hora}`,
      };
    }
  }

  return VEREDICTO_LIBRE;
};

/** ¿Cabe el grupo en la sala? Es un aviso, no un impedimento: el aforo real es un texto. */
export const superaCapacidad = (location: Location | undefined, personas: number): boolean => {
  const maximo = Number(location?.schedule?.settings?.maximumCapacity ?? 0);
  return maximo > 0 && personas > maximo;
};

export type Alternativa = { location: Location; ajustada: boolean };

/**
 * Dos espacios son intercambiables si se reservan igual.
 *
 * Sin esto, a quien reserva una sala de reuniones se le ofrecía la zona de
 * coworking como alternativa —cabe, técnicamente— y una visita para conocer el
 * espacio se mezclaba con las salas. Son cosas distintas aunque el hueco encaje.
 */
const mismoTipoDeEspacio = (a: Location | undefined, b: Location | undefined): boolean => {
  const x = a?.schedule?.settings;
  const y = b?.schedule?.settings;
  return (
    Boolean(x?.isReservationWholeDay) === Boolean(y?.isReservationWholeDay) &&
    Boolean(x?.personHasSpecificPosition) === Boolean(y?.personHasSpecificPosition) &&
    Boolean(x?.isEndDateEnable) === Boolean(y?.isEndDateEnable)
  );
};

/**
 * Otras salas donde sí cabría esa fecha, a la misma hora.
 *
 * Se ordenan de menor a mayor capacidad para proponer primero la que menos
 * espacio desperdicia — reservar el teatro para dos personas no es una
 * alternativa razonable aunque técnicamente quepa.
 */
export const salasAlternativas = ({
  salas,
  excluir,
  fecha,
  franjas,
  personas,
  comoLa,
}: {
  salas: Location[];
  excluir: string;
  fecha: string;
  franjas: string[];
  personas: number;
  /** Solo se proponen espacios que se reserven igual que este. */
  comoLa?: Location;
}): Alternativa[] =>
  salas
    .filter((sala) => sala.id !== excluir)
    .filter((sala) => !comoLa || mismoTipoDeEspacio(sala, comoLa))
    .filter((sala) => !superaCapacidad(sala, personas))
    .filter((sala) => evaluarFecha({ location: sala, fecha, franjas, personas }).estado === 'libre')
    .map((sala) => ({
      location: sala,
      ajustada: Number(sala.schedule?.settings?.maximumCapacity ?? 0) >= personas,
    }))
    .sort(
      (a, b) =>
        Number(a.location.schedule?.settings?.maximumCapacity ?? 0) -
        Number(b.location.schedule?.settings?.maximumCapacity ?? 0)
    );

/** "miércoles y jueves", para contarlo en una frase. */
export const enumerarDias = (dias: number[]): string => {
  const nombres = [...dias]
    .sort((a, b) => a - b)
    .map((d) => NOMBRES_DIAS.find((n) => n.indice === d)?.largo ?? '');
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
};

export const claveFecha = (fecha: Date): string => format(fecha, 'yyyy-MM-dd');
